import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  Palette,
  MapPin,
  Tag,
  Briefcase,
  MessageSquare,
  CheckCircle2,
  SlidersHorizontal,
  X,
  Check,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { freelanceGigsApi, FreelanceGigFeedRow } from '../services/freelanceGigsApi';
import api from '../services/api';
import { useGigSectorPrefs } from '../hooks/useGigSectorPrefs';
import './FreelanceGigs.scss';

/**
 * Employee-facing feed of all currently published Freelance Gigs.
 * Supports per-sector filtering saved to localStorage.
 */
const FreelanceBrowse: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [gigs, setGigs] = useState<FreelanceGigFeedRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Sector preference state (persisted in DB via hook)
  const { selectedSectors, setSectors } = useGigSectorPrefs();
  const [allSectors, setAllSectors] = useState<string[]>([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [staged, setStaged] = useState<string[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);

  // Load available sectors from cv_templates
  useEffect(() => {
    api.get('/cv-templates?grouped=true')
      .then(res => {
        const data = res.data?.data ?? res.data ?? {};
        const workFields: string[] = (data.work_field ?? []).map((t: any) => t.value as string);
        setAllSectors(workFields);
      })
      .catch(() => {});
  }, []);

  // Close panel on outside click
  useEffect(() => {
    if (!panelOpen) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [panelOpen]);

  const load = useCallback(async (sectors: string[]) => {
    setLoading(true);
    try {
      const { gigs } = await freelanceGigsApi.publishedFeed(100, 0, sectors.length > 0 ? sectors : undefined);
      setGigs(gigs);
    } catch (err) {
      console.error('Failed to load gigs feed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.user_type === 'employee') load(selectedSectors);
  }, [isAuthenticated, user, load, selectedSectors]);

  const openPanel = () => {
    setStaged([...selectedSectors]);
    setPanelOpen(true);
  };

  const applyPanel = () => {
    setSectors(staged);
    setPanelOpen(false);
  };

  const removeSector = (s: string) => {
    setSectors(selectedSectors.filter(x => x !== s));
  };

  const clearAll = () => {
    setSectors([]);
  };

  if (isLoading) return <div className="freelance-page-loading">Kraunama...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.user_type !== 'employee') return <Navigate to="/" replace />;

  return (
    <div className="freelance-gigs-page">
      <div className="freelance-page-header">
        <div className="freelance-page-title">
          <Palette size={22} />
          <h1>Freelance Gigs</h1>
        </div>
        <Link to="/freelance/my-responses" className="btn btn-secondary">
          <Briefcase size={16} /> Mano atsakymai
        </Link>
      </div>

      <p className="freelance-empty-hint freelance-empty-hint--subtitle">
        Sutinkate priimti freelance užduotis? Pažymėkite tai savo CV, kad apie kiekvieną
        naują tinkamą Gig sužinotumėte el. paštu.
      </p>

      {/* ── Sector filter bar ── */}
      <div className="gig-sector-bar">
        <div className="gig-sector-bar__chips">
          {selectedSectors.length === 0 ? (
            <span className="gig-sector-chip gig-sector-chip--all">Visos sritys</span>
          ) : (
            selectedSectors.map(s => (
              <span key={s} className="gig-sector-chip gig-sector-chip--active">
                {s}
                <button
                  type="button"
                  className="gig-sector-chip__remove"
                  onClick={() => removeSector(s)}
                  title="Pašalinti"
                >
                  <X size={12} />
                </button>
              </span>
            ))
          )}
        </div>
        <div className="gig-sector-bar__actions">
          {selectedSectors.length > 0 && (
            <button type="button" className="gig-sector-clear" onClick={clearAll} title="Rodyti visas">
              <X size={14} /> Išvalyti
            </button>
          )}
          <div className="gig-sector-picker-wrap" ref={panelRef}>
            <button type="button" className="gig-sector-pick-btn" onClick={openPanel}>
              <SlidersHorizontal size={15} /> Pasirinkti sritis
            </button>

            {panelOpen && (
              <div className="gig-sector-panel">
                <div className="gig-sector-panel__header">
                  <span>Mano sritys</span>
                  <button type="button" onClick={() => setPanelOpen(false)}><X size={16} /></button>
                </div>
                <div className="gig-sector-panel__list">
                  {allSectors.map(s => {
                    const checked = staged.includes(s);
                    return (
                      <label key={s} className={`gig-sector-option ${checked ? 'checked' : ''}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setStaged(prev =>
                              checked ? prev.filter(x => x !== s) : [...prev, s]
                            )
                          }
                        />
                        <span className="gig-sector-option__box">
                          {checked && <Check size={11} strokeWidth={3} />}
                        </span>
                        {s}
                      </label>
                    );
                  })}
                </div>
                <div className="gig-sector-panel__footer">
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => setStaged([])}
                  >
                    Išvalyti
                  </button>
                  <button
                    type="button"
                    className="btn-primary btn-sm"
                    onClick={applyPanel}
                  >
                    Taikyti ({staged.length > 0 ? staged.length : 'visos'})
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="freelance-page-loading">Kraunama...</div>
      ) : gigs.length === 0 ? (
        <div className="freelance-empty-state">
          <div className="freelance-empty-icon"><Palette size={42} /></div>
          <h2>{selectedSectors.length > 0 ? 'Pasirinktose srityse gigų nėra' : 'Šiuo metu naujų Gigų nėra'}</h2>
          <p>
            {selectedSectors.length > 0
              ? 'Pabandykite pasirinkti kitas sritis arba išvalykite filtrą.'
              : 'Ateikite vėliau arba pažymėkite savo CV, kad gautumėte el. laiškus.'}
          </p>
          {selectedSectors.length > 0 && (
            <button type="button" className="btn btn-secondary" onClick={clearAll}>
              Rodyti visas sritis
            </button>
          )}
        </div>
      ) : (
        <div className="freelance-gigs-grid">
          {gigs.map((g) => {
            const hasUnread = g.unread_count > 0 && g.my_response_id != null;
            const linkTo = hasUnread
              ? `/freelance/messages/${g.my_response_id}`
              : `/freelance/browse/${g.id}`;
            const responded = g.my_response_status === 'interested';

            return (
              <Link key={g.id} to={linkTo} className="freelance-gig-card">
                {(hasUnread || responded) && (
                  <div className="freelance-gig-card-top">
                    {responded && (
                      <span className="freelance-responded-chip">
                        <CheckCircle2 size={12} /> Atsiliepėte
                      </span>
                    )}
                    {hasUnread && (
                      <span
                        className="freelance-unread-pill"
                        title={`${g.unread_count} neperskaitytos žinutės iš darbdavio`}
                      >
                        <MessageSquare size={11} />
                        {g.unread_count}
                      </span>
                    )}
                  </div>
                )}

                <h3 className="freelance-gig-title">{g.title}</h3>
                <p className="freelance-gig-desc">{g.description}</p>
                <div className="freelance-gig-meta">
                  {g.work_fields.length > 0 && (
                    <span className="freelance-gig-tag"><Tag size={12} /> {g.work_fields.join(', ')}</span>
                  )}
                  <span className="freelance-gig-tag"><MapPin size={12} />
                    {g.nationwide ? 'Visoje Lietuvoje' : (g.city || 'Nenurodyta')}
                  </span>
                  {g.budget_text && (
                    <span className="freelance-gig-tag">{g.budget_text}</span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FreelanceBrowse;
